import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { motion } from 'framer-motion';
import { FaProjectDiagram, FaUserCircle, FaCog, FaSignOutAlt, FaHome, FaChartBar, FaTags, FaBug, FaFileAlt, FaDatabase, FaImage, FaInbox, FaHeart, FaBriefcase } from 'react-icons/fa';
import ProjectManager from '../Components/Admin/ProjectManager';
import ProfileManager from '../Components/Admin/ProfileManager';
import AnalyticsDashboard from '../Components/Admin/AnalyticsDashboard';
import TagManager from '../Components/Admin/TagManager';
import FirestoreDiagnostic from '../Components/Admin/FirestoreDiagnostic';
import ContentManager from '../Components/Admin/ContentManager';
import SettingsManager from '../Components/Admin/SettingsManager';
import DynamicFieldManager from '../Components/Admin/DynamicFieldManager';
import ImageManager from '../Components/Admin/ImageManager';
import SubmissionsManager from '../Components/Admin/SubmissionsManager';
import StatsManager from '../Components/Admin/StatsManager';
import ExperienceManager from '../Components/Admin/ExperienceManager';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    // Check Firebase Auth state (more reliable than localStorage)
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is signed in
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem('adminAuthenticated', 'true');
        console.log('✅ User authenticated:', user.email);
      } else {
        // User is signed out
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuthenticated');
        navigate('/admin/login');
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [navigate, auth]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminAuthenticated');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: FaChartBar },
    { id: 'projects', label: 'Projects', icon: FaProjectDiagram },
    { id: 'experience', label: 'Experience', icon: FaBriefcase },
    { id: 'submissions', label: 'Messages', icon: FaInbox },
    { id: 'stats', label: 'Stats', icon: FaHeart },
    { id: 'content', label: 'Content', icon: FaFileAlt },
    { id: 'profile', label: 'Profile', icon: FaUserCircle },
    { id: 'images', label: 'Images', icon: FaImage },
    { id: 'settings', label: 'Settings', icon: FaCog },
    { id: 'fields', label: 'Custom Fields', icon: FaDatabase },
    { id: 'tags', label: 'Tags', icon: FaTags },
    { id: 'diagnostic', label: 'Diagnostic', icon: FaBug },
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1d1836] via-[#100d25] to-[#0a0820]">
      {/* Header */}
      <header className="bg-[#1d1836]/80 backdrop-blur-md border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-2xl font-bold text-white"
              >
                Admin Dashboard
              </motion.h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg transition"
              >
                <FaHome />
                <span>View Site</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition"
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'bg-[#1d1836] text-gray-400 hover:text-white hover:bg-[#251e47]'
                }`}
              >
                <Icon />
                <span>{tab.label}</span>
              </motion.button>
            );
          })}
        </div>

        {/* Content Area */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'analytics' && <AnalyticsDashboard />}
          {activeTab === 'projects' && <ProjectManager />}
          {activeTab === 'experience' && <ExperienceManager />}
          {activeTab === 'submissions' && <SubmissionsManager />}
          {activeTab === 'stats' && <StatsManager />}
          {activeTab === 'content' && <ContentManager />}
          {activeTab === 'images' && <ImageManager />}
          {activeTab === 'profile' && <ProfileManager />}
          {activeTab === 'settings' && <SettingsManager />}
          {activeTab === 'fields' && <DynamicFieldManager />}
          {activeTab === 'tags' && <TagManager />}
          {activeTab === 'diagnostic' && <FirestoreDiagnostic />}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
